import React, { useMemo } from 'react'
import clsx from 'clsx'

type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'card' | 'link' | 'fade' | 'footer'
type ButtonSize = 'small' | 'medium' | 'large' | 'box' | 'none'

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void
  type?: 'button' | 'submit' | 'reset'
  disabled?: boolean
  children?: React.ReactNode
  variant?: ButtonVariant
  size?: ButtonSize
  position?: string
  marginTop?: string
  marginLeft?: string
  left?: string
  right?: string
  border?: string
  color?: string
  className?: string
}

const variantClasses: Record<ButtonVariant, string> = {
  primary: 'bg-moss-green text-on-primary rounded-[6px] hover:bg-moss-green-highlight border-none font-radio focus-visible:outline-2 focus-visible:outline-purple focus-visible:outline-offset-2',
  secondary:
    'bg-transparent text-moss-green border-[1.5px] border-moss-green rounded-[6px] hover:bg-moss-green/8 hover:text-text transition-colors font-radio disabled:opacity-50 disabled:cursor-not-allowed focus-visible:outline-2 focus-visible:outline-purple focus-visible:outline-offset-2',
  danger: 'bg-purple-shade text-on-primary rounded-[6px] hover:bg-purple border-none font-radio focus-visible:outline-2 focus-visible:outline-purple focus-visible:outline-offset-2',
  card: 'bg-card text-text rounded-[6px] hover:bg-moss-green hover:text-on-primary font-radio border-none disabled:opacity-50 disabled:cursor-not-allowed active:bg-moss-green active:text-on-primary [&.selected]:bg-moss-green [&.selected]:text-on-primary focus-visible:outline-2 focus-visible:outline-purple focus-visible:outline-offset-2',
  link: 'bg-transparent text-purple-shade border-none underline decoration-purple rounded-none hover:decoration-purple-shade hover:decoration-2 font-radio',
  fade: 'flex items-center gap-2 bg-card text-text border-none px-5 py-3 transition-colors hover:bg-mint font-radio focus-visible:outline-2 focus-visible:outline-purple focus-visible:outline-offset-2',
  footer:
    'bg-moss-green text-on-primary rounded-[6px] border-none transition-colors hover:bg-moss-green-highlight disabled:opacity-50 disabled:cursor-not-allowed font-radio focus-visible:outline-2 focus-visible:outline-purple focus-visible:outline-offset-2',
}

const sizeClasses: Record<ButtonSize, string> = {
  small: 'px-3 py-1.5 text-[0.9rem] min-w-[80px]',
  medium: 'px-[18px] py-3 text-base min-w-[100px]',
  large: 'px-5 py-3.5 text-[1.2rem] min-w-[120px]',
  box: 'px-6 py-[18px] text-[1.2rem] min-w-[120px] m-3 w-[250px] h-[100px]',
  none: 'px-1.5 py-0',
}

const Button = ({
  onClick,
  type = 'button',
  disabled = false,
  children,
  variant = 'primary',
  size = 'medium',
  position = undefined,
  marginTop = undefined,
  marginLeft = undefined,
  left = undefined,
  right = undefined,
  border = undefined,
  color = undefined,
  className = undefined,
  ...rest
}: ButtonProps): React.ReactElement => {
  const handleClick = (e: React.MouseEvent<HTMLButtonElement>): void => {
    if (disabled) {
      e.preventDefault()
      return
    }
    e.currentTarget.blur()
    if (onClick) onClick(e)
  }

  // maybe refactor this so this is possible through className
  const dynamicStyle = useMemo((): React.CSSProperties | undefined => {
    const s: Record<string, string> = {}
    if (position) s.position = position
    if (marginTop) s.marginTop = marginTop
    if (marginLeft) s.marginLeft = marginLeft
    if (left) s.left = left
    if (right) s.right = right
    if (border) s.border = border
    if (color) s.color = color
    return Object.keys(s).length > 0 ? (s as React.CSSProperties) : undefined
  }, [position, marginTop, marginLeft, left, right, border, color])

  return (
    <button
      type={type}
      onClick={handleClick}
      className={clsx(
        'cursor-pointer inline-block disabled:opacity-60 disabled:cursor-not-allowed',
        variantClasses[variant],
        sizeClasses[size],
        className
      )}
      style={dynamicStyle}
      disabled={disabled}
      {...rest}
    >
      {children}
    </button>
  )
}

export default Button
